import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import CookieForm from './CookieForm';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { checkSubscription } from '../utils/subscriptionUtils';
import InstallPWA from './InstallPWA';
import ToggleSwitch from './ToggleSwitch';
import { Lightbulb } from 'lucide-react';
import { setCookie, getCookie, round, formatNumber, calculateArea } from '../utils/calculatorUtils';
import { lengthToDisplay, lengthFromDisplay, weightToDisplay, weightFromDisplay, lengthUnitLabel, weightUnitLabel, densityToDisplay, densityUnitLabel } from '../utils/unitConversion';
import { getDefaultReference } from '../utils/defaultReferenceConfig';
import { useAppSettings } from '../hooks/useAppSettings';
import ProCalculatorWrapper from './ProCalculatorWrapper';
import '../styles/Calculator.css';
import '../styles/Preview.css';
import '../styles/Settings.css';
import AmazonLinksSection from './sections/AmazonLinksSection';
import { logUsageEvent } from '../utils/logUsageEvent';
import copyToClipboard from '../utils/copyToClipboard';
import usePageTitle from '../hooks/usePageTitle';
import SectionHelp from './SectionHelp';

const Calculator = ({ 
  isPreview = false, 
  user = null, 
  isPro = false, 
  isDarkMode = true, 
  setIsDarkMode = () => {}, 
  calculatorVersion = 'simple',
  setCalculatorVersion = () => {},
  activeSection = 'reference',
  setActiveSection = () => {},
  onStateChange = () => {}, 
  savedState = null,
}) => {
  
  usePageTitle(
    calculatorVersion === 'pro' ? 'Pro Royal Icing Calculator: Colors, Orders & Export' : 'Basic Calculator',
    calculatorVersion === 'pro'
      ? 'Plan icing for multiple cookie shapes, colors, and full orders, plus Excel & PDF export. Free to use.'
      : 'Calculate how much royal icing you need by cookie size and count. Free to use.'
  );
  const authContext = useAuth();
  // Use the prop user if provided, otherwise use the context user
  const currentUser = user || authContext?.user;
  const navigate = useNavigate();

  // Unit preferences — persisted in localStorage, display-only (canonical storage is always inches/grams)
  const [isMetric, setIsMetric] = useState(() => {
    try { return localStorage.getItem('basic_isMetric') === 'true'; } catch { return false; }
  });
  const [isMetricWeight, setIsMetricWeight] = useState(() => {
    try {
      const stored = localStorage.getItem('basic_isMetricWeight');
      return stored !== null ? stored === 'true' : false; // new users default to oz
    } catch { return false; }
  });

  // Persist unit preferences
  useEffect(() => {
    try { localStorage.setItem('basic_isMetric', String(isMetric)); } catch { /* noop */ }
  }, [isMetric]);
  useEffect(() => {
    try { localStorage.setItem('basic_isMetricWeight', String(isMetricWeight)); } catch { /* noop */ }
  }, [isMetricWeight]);

  // Get default reference — live from admin settings, fallback to hardcoded BASE_DEFAULTS
  const { settings: appSettings } = useAppSettings();
  const currentDefaultReference = useMemo(() => {
    const drc = appSettings?.default_reference_cookie;
    if (drc && drc.width && drc.length && drc.weightNoIcing && drc.weight) {
      return { width: String(drc.width), length: String(drc.length), weightNoIcing: String(drc.weightNoIcing), weight: String(drc.weight) };
    }
    return getDefaultReference();
  }, [appSettings?.default_reference_cookie]);
  
  // State for calculator inputs
  const [reference, setReference] = useState({ width: '', length: '', weightNoIcing: '', weight: '' });
  const [target, setTarget] = useState({ width: '', length: '', count: '1' });
  const [useDefaultReference, setUseDefaultReference] = useState(true); // Default ON
  const [resultText, setResultText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Refs for calculations
  const referenceAreaRef = useRef(0);
  const referenceIcingPerAreaRef = useRef(0);

  // --- BEGIN REVISED CALCULATION useEffect --- 
  useEffect(() => {
    // All internal state is canonical: inches for length, grams for weight
    const currentReference = useDefaultReference ? currentDefaultReference : reference;
    
    // Parse reference values safely (canonical: inches, grams)
    const refWidth = parseFloat(currentReference.width) || 0;
    const refLength = parseFloat(currentReference.length) || 0;
    const refWeightNoIcing = parseFloat(currentReference.weightNoIcing) || 0;
    const refWeight = parseFloat(currentReference.weight) || 0;

    // Parse target values safely (canonical: inches)
    const targetWidth = parseFloat(target.width) || 0;
    const targetLength = parseFloat(target.length) || 0;
    const targetCount = parseInt(target.count, 10) || 0;

    // Calculate reference area in sq inches
    const refArea = calculateArea('rectangle', refWidth, refLength);
    referenceAreaRef.current = refArea;
    
    // Calculate reference icing weight (grams)
    const refIcingWeight = refWeight - refWeightNoIcing;

    // Calculate icing per square unit (grams per square inch)
    let icingPerArea = 0;
    if (refArea > 0 && refIcingWeight > 0) {
      icingPerArea = refIcingWeight / refArea;
    }
    referenceIcingPerAreaRef.current = icingPerArea;

    // Calculate target area in sq inches
    const targetArea = calculateArea('rectangle', targetWidth, targetLength);

    // Calculate total icing needed (grams)
    const totalIcingGrams = targetArea * icingPerArea * targetCount;

    // --- Display unit labels ---
    const lenLabel = lengthUnitLabel(isMetric);
    const wLabel = weightUnitLabel(isMetricWeight);
    const densLabel = densityUnitLabel(isMetric, isMetricWeight);

    // Format results using user's preferred units
    let newResultText = '';
    if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && icingPerArea > 0) {
      const icingPerTargetGrams = targetArea * icingPerArea;

      // Display-converted values for the result text
      const displayRefW = formatNumber(lengthToDisplay(refWidth, isMetric));
      const displayRefL = formatNumber(lengthToDisplay(refLength, isMetric));
      const displayTargetW = formatNumber(lengthToDisplay(targetWidth, isMetric));
      const displayTargetL = formatNumber(lengthToDisplay(targetLength, isMetric));
      const displayDensity = formatNumber(densityToDisplay(icingPerArea, isMetric, isMetricWeight), 4);
      const displayPerCookie = formatNumber(weightToDisplay(icingPerTargetGrams, isMetricWeight), 2);

      // --- Format Total in BOTH unit systems (always show both) ---
      // Metric total
      let totalMetricFormatted;
      if (totalIcingGrams >= 1000) {
        const kg = Math.floor(totalIcingGrams / 1000);
        const g = round(totalIcingGrams % 1000, 1);
        totalMetricFormatted = `${kg} kg ${g} g (${formatNumber(totalIcingGrams, 2)} g)`;
      } else {
        totalMetricFormatted = `${formatNumber(totalIcingGrams, 2)} g`;
      }

      // Imperial total
      const totalIcingOz = totalIcingGrams / 28.34952;
      let totalImperialFormatted;
      if (totalIcingOz >= 16) {
        const lb = Math.floor(totalIcingOz / 16);
        const oz = round(totalIcingOz % 16, 1);
        totalImperialFormatted = `${lb} lb ${oz} oz (${formatNumber(totalIcingOz, 2)} oz)`;
      } else {
        totalImperialFormatted = `${formatNumber(totalIcingOz, 2)} oz`;
      }

      // Show user's preferred unit first, then the other
      const primaryTotal = isMetricWeight ? totalMetricFormatted : totalImperialFormatted;
      const secondaryTotal = isMetricWeight ? totalImperialFormatted : totalMetricFormatted;

      const refSizeText = `(${displayRefW} x ${displayRefL} ${lenLabel})`;
      const targetSizeText = `(${displayTargetW} x ${displayTargetL} ${lenLabel})`;

      newResultText = 
        `Test Cookie ${useDefaultReference ? '(Default)' : ''}: ${refSizeText}
` + 
        `Icing coverage rate: ${displayDensity} ${densLabel}

` + 
        `For ${targetCount} Cookie${targetCount !== 1 ? 's' : ''} to Decorate: ${targetSizeText}
` + 
        `Each cookie needs: ${displayPerCookie} ${wLabel}

` + 
        `Total Icing Needed:
` + 
        `<span class="total-amount">${primaryTotal}</span>
` + 
        `<span class="imperial-weight">${secondaryTotal}</span>`;

    } else if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && refArea > 0 && refIcingWeight <= 0) {
      newResultText = 'Your test cookie shows zero icing weight.\nPlease check the before and after weights.';
    } else if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && refArea <= 0) {
       newResultText = 'Your test cookie has zero area.\nPlease enter valid width and length.';
    } else {
      newResultText = 'Fill in your test cookie measurements above to see how much icing you need.';
    }

    setResultText(newResultText);

  }, [reference, target, useDefaultReference, currentDefaultReference, isMetric, isMetricWeight]); // Dependencies
  // --- END REVISED CALCULATION useEffect --- 

  // Load saved values from cookies - Simplified: Remove unit loading
  const loadSavedValues = () => {
    try {
      const storedReference = getCookie('reference');
      const storedTarget = getCookie('target');
      const storedUseDefault = getCookie('useDefaultReference');

      // Default values
      let parsedReference = { width: '', length: '', weightNoIcing: '', weight: '' };
      let parsedTarget = { width: '', length: '', count: '1' };
      let parsedUseDefault = true; // default on for new visitors

      // Parse stored values (assumed to be in inches/grams)
      if (storedReference) {
        try {
          parsedReference = JSON.parse(storedReference);
        } catch (e) {
        }
      }
      if (storedTarget) {
        try {
          parsedTarget = JSON.parse(storedTarget);
        } catch (e) {
        }
      }
      if (storedUseDefault !== null && storedUseDefault !== undefined && storedUseDefault !== '') {
        parsedUseDefault = storedUseDefault === 'true';
      }

      setReference(parsedReference);
      setTarget(parsedTarget);
      setUseDefaultReference(parsedUseDefault);

    } catch (error) {
    }
  };

  const copyTimeoutRef = useRef(null);

  // Load values on initial mount
  useEffect(() => {
    loadSavedValues();
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  // Function to copy results to clipboard
  const handleCopyResults = useCallback(() => {
    if (!resultText) return;
    const plainText = resultText.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '') +
      '\n\nTips:\nPro Tip: Pad Your Numbers! This calculator estimates the flood icing needed for your project.\nTo ensure you don\'t run short, we recommend adding 1-2 extra cookies to your count per shape—this gives you a safety net if you need to scrape and re-ice.\n\nDon\'t forget the details: If you\'re adding text, fine details, or heavy florals, remember\nto mix an extra 1-3 oz of icing per cookie. Large floral designs require significantly more volume, so plan your batches accordingly!' +
      '\n\n---\nResults provided by TheIcingCalculator.com';
    // Log immediately — user clearly intended the action regardless of clipboard API result
    logUsageEvent({ eventType: 'copy_results', calculator: 'basic', user: currentUser });

    // Always show feedback — iOS may report false on repeat clicks
    copyToClipboard(plainText).then(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    });
  }, [resultText, currentUser]);

  // Handle default reference toggle
  const handleDefaultReferenceToggle = (isUsingDefault) => {
    setUseDefaultReference(isUsingDefault);
    // If switching to default, immediately apply fixed default values
    if (isUsingDefault) {
      // No need to get defaults dynamically, they are fixed
      setReference({ 
        width: currentDefaultReference.width, 
        length: currentDefaultReference.length, 
        weightNoIcing: currentDefaultReference.weightNoIcing, 
        weight: currentDefaultReference.weight 
      });
    } else {
       // Optionally clear reference fields or keep last custom values
       // Let's keep the last custom values for now
    }
    setCookie('useDefaultReference', isUsingDefault); // Save toggle state
  };

  // Handle reference change — convert display value back to canonical (inches/grams)
  const handleReferenceChange = (field, value) => {
    let canonicalValue = value;
    if (field === 'width' || field === 'length') {
      canonicalValue = lengthFromDisplay(value, isMetric);
    } else if (field === 'weightNoIcing' || field === 'weight') {
      canonicalValue = weightFromDisplay(value, isMetricWeight);
    }
    setReference(prev => ({ ...prev, [field]: canonicalValue === '' ? '' : String(canonicalValue) }));
  };

  // Handle target change — convert display value back to canonical (inches)
  const handleTargetChange = (field, value) => {
    let canonicalValue = value;
    if (field === 'width' || field === 'length') {
      canonicalValue = lengthFromDisplay(value, isMetric);
    }
    setTarget(prev => ({ ...prev, [field]: canonicalValue === '' ? '' : String(canonicalValue) }));
  };

  // Save values whenever they change
  useEffect(() => {
    setCookie('reference', JSON.stringify(reference));
    setCookie('target', JSON.stringify(target));
  }, [reference, target]);

  const resetAll = () => {
    // Reset to initial state, respecting fixed units
    setReference({ width: '', length: '', weightNoIcing: '', weight: '' });
    setTarget({ width: '', length: '', count: '1' });
    setUseDefaultReference(false);
    setResultText('');
    setCopySuccess(false);
    referenceAreaRef.current = 0;
    referenceIcingPerAreaRef.current = 0;
    
    // Clear cookies
    setCookie('reference', '', 0);
    setCookie('target', '', 0);
    setCookie('useDefaultReference', 'false', 0);

  };

  // Render a preview banner if the user is not logged in
  const renderPreviewBanner = () => {
    if (isPreview) {
      return (
        <div className="preview-banner">
          <div className="preview-content">
            <h3>Login Required</h3>
            <p>You need to create a free account to use The Icing Calculator.</p>
            <p>Sign up now to access all basic features!</p>
            <div className="preview-buttons">
              <button 
                className="preview-button login"
                onClick={() => navigate('/login?redirect=/basic')}
              >
                Login
              </button>
              <button 
                className="preview-button signup"
                onClick={() => {
                  // Navigate to login page with signup tab active
                  setCookie('initialAuthTab', 'signup');
                  navigate('/login?redirect=/basic');
                }}
              >
                Sign Up Free
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`calculator ${isDarkMode ? 'dark-mode' : 'light-mode'}`} data-testid="basic-calculator">
      <InstallPWA />
      
      {/* Removed the entire action-bar div */}
      
      <p className="calc-eyebrow">{calculatorVersion === 'simple' ? 'BASIC CALCULATOR' : 'PRO CALCULATOR'}</p>
      {renderPreviewBanner()}
      
      
      {calculatorVersion === 'simple' ? (
        <>
          {/* Unit preference box — sits above the sample cookie card */}
          <div className="unit-prefs-box">
            <h3 className="unit-prefs-title">Measurement Units</h3>
            <p className="unit-prefs-desc">Choose whether you measure in inches or millimeters, and ounces or grams.</p>
            <div className="unit-toggles">
              <div className="unit-toggle-group">
                <label className="reference-toggle-label">inches</label>
                <ToggleSwitch isOn={isMetric} onToggle={(val) => setIsMetric(val)} />
                <label className="reference-toggle-label">mm</label>
              </div>
              <div className="unit-toggle-group">
                <label className="reference-toggle-label">oz</label>
                <ToggleSwitch isOn={isMetricWeight} onToggle={(val) => setIsMetricWeight(val)} />
                <label className="reference-toggle-label">grams</label>
              </div>
            </div>
          </div>

          <div className="reference-section">
            <div className="section-header">
              <h2 className="section-title">Your Test Cookie</h2>
            </div>

            <p className="reference-subtitle">Tell us how much icing you use on one cookie. We will use that to calculate the rest.</p>

            <p className="reference-quick-start-hint">
              No scale handy? Turn on <strong>Use Default (Quick Start)</strong> and we'll fill in a test cookie for you.
            </p>

            <SectionHelp sectionId="basicCalculator" isMobile={false} />

            <div 
              className="reference-toggle-container" 
              style={{ display: 'flex', alignItems: 'center', gap: '1ch' }}
            >
              <label htmlFor="defaultReferenceToggle" className="reference-toggle-label">
                Use Default (Quick Start)
              </label>
              <ToggleSwitch 
                switchId="defaultReferenceToggle"
                isOn={useDefaultReference} 
                onToggle={handleDefaultReferenceToggle} 
                disabled={isPreview}
              />
            </div>

            {!useDefaultReference && (
              <CookieForm
                hideTitle={true}
                width={lengthToDisplay(reference.width, isMetric)}
                length={lengthToDisplay(reference.length, isMetric)}
                weightNoIcing={weightToDisplay(reference.weightNoIcing, isMetricWeight)}
                weight={weightToDisplay(reference.weight, isMetricWeight)}
                isMetric={isMetric}
                isMetricWeight={isMetricWeight}
                onChange={handleReferenceChange}
                disabled={isPreview}
              />
            )}
          </div>

          <CookieForm
            title="Cookies to Decorate"
            width={lengthToDisplay(target.width, isMetric)}
            length={lengthToDisplay(target.length, isMetric)}
            count={target.count}
            isMetric={isMetric}
            isMetricWeight={isMetricWeight}
            onChange={handleTargetChange}
            onReset={() => setTarget({ width: '', length: '', count: '1' })}
            disabled={isPreview}
          />

          <div className="basic-calc-footer" style={{display: 'none'}}>
          </div>

          <div className="basic-tip-jar-cta">
            <p className="basic-tip-jar-text">
              The Basic Calculator is free and always will be. If it saves you time, a tip helps keep the lights on.
            </p>
            <a
              href="https://buy.stripe.com/4gMdR85j5gftgsaaiB0RG01"
              target="_blank"
              rel="noopener noreferrer"
              className="basic-tip-jar-btn"
            >
              Leave a Tip
            </a>
          </div>

          <div className="results">
            <div className="results-header">
              <h2>Icing Plan</h2>
              <button 
                className="footer-button"
                onClick={handleCopyResults}
                disabled={!resultText || isPreview}
                title={resultText ? "Copy results to clipboard" : "No results to copy"}
              >
                <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span className="button-label">{copySuccess ? 'Copied!' : 'Copy Icing Totals'}</span>
              </button>
            </div>
            <p dangerouslySetInnerHTML={{ __html: resultText.split('\n').map(line => line + '<br />').join('') }} />
            {resultText && !resultText.startsWith('Fill') && !resultText.startsWith('Your test') && (
              <div className="basic-icing-tip">
                <span className="basic-icing-tip-icon"><Lightbulb size={16} /></span>
                <div>
                  <strong>Heads up:</strong> Total reflects flood icing only. Recommended: Add 1–2 cookies per shape to your count for "scrape insurance." For detail work or lettering, add 1–3 oz. Note: High-volume florals will require additional icing beyond this estimate.
                </div>
              </div>
            )}
          </div>

          {/* Amazon Links Section for Basic Calculator */}
          <AmazonLinksSection title="Recommended Decorating Tools" />
        </>
      ) : (
        <ProCalculatorWrapper 
          isDarkMode={isDarkMode} 
          isPreview={isPreview} 
          user={currentUser} 
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onStateChange={onStateChange} 
          savedState={savedState} 
        />
      )}
    </div>
  );
};

export default Calculator;
